import { FC, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/_atoms/button";
import { IconArrowTinyRight } from "@/components/_icons/IconArrowTinyRight";
import { IconCircleGroup } from "@/components/_icons/IconCircleGroup";
import { IconPlus } from "@/components/_icons/IconPlus";
import { MenuDotsButton } from "@/components/_molecules/menu-dots-button";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useGetLabelGroup } from "@/domain/label/hooks/api/use-get-label-group";

import { LabelGroupActionButton } from "./label-group-action-button/label-group-action-button";
import { type LabelAssignmentContext } from "./label-assignment-context";
import { LabelCreateForm } from "./label-create-form";
import { LabelGroupEditForm } from "./label-group-edit-form";
import { LabelListItem } from "./label-list-item";

interface LabelGroupListItemProps {
  labelGroupId: string;
  workspaceId: string;
  labelAssignmentContext?: LabelAssignmentContext;
}

export const LabelGroupListItem: FC<LabelGroupListItemProps> = ({
  labelGroupId,
  workspaceId,
  labelAssignmentContext,
}) => {
  const { hasPermission: hasPermissionToManageWorkspace } = useCheckPermission({
    resource: "workspace",
    resourceId: workspaceId,
    action: "manage",
  });
  const { labelGroup } = useGetLabelGroup({ id: labelGroupId });

  const [showChildren, setShowChildren] = useState(labelGroup?.allowMultiSelect ?? true);
  const [showCreateLabelForm, setShowCreateLabelForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [localAllowMultiSelect, setLocalAllowMultiSelect] = useState(labelGroup?.allowMultiSelect ?? true);

  const childLabelList = useMemo(() => {
    if (!labelGroup) {
      return [];
    }
    return labelGroup.labelList.slice().sort((a, b) => {
      if (!a || !b) {
        return 0;
      }
      return a.title.localeCompare(b.title);
    });
  }, [labelGroup]);

  function handleLabelHasBeenAdded() {
    setShowCreateLabelForm(false);
  }

  const serverAllowMultiSelect = labelGroup?.allowMultiSelect;
  useEffect(() => {
    if (serverAllowMultiSelect !== undefined && !editMode) {
      setLocalAllowMultiSelect(serverAllowMultiSelect);
      setShowChildren(serverAllowMultiSelect);
    }
  }, [serverAllowMultiSelect, editMode]);

  useEffect(() => {
    if (labelGroup?.labelList.length === 0) {
      if (hasPermissionToManageWorkspace) {
        setShowCreateLabelForm(true);
      }
      setShowChildren(true);
    } else {
      setShowCreateLabelForm(false);
    }
  }, [labelGroup, hasPermissionToManageWorkspace]);

  useEffect(() => {
    if (editMode) {
      setShowCreateLabelForm(false);
    }
  }, [editMode]);

  useEffect(() => {
    if (showCreateLabelForm) {
      setEditMode(false);
    }
  }, [showCreateLabelForm]);

  if (!labelGroup) {
    return <></>;
  }

  return (
    <div className="flex flex-col rounded-md border border-border">
      {!editMode && (
        <div className="flex w-full flex-row items-center gap-1 rounded-md px-2 py-1 hover:bg-muted/60">
          <div className="shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowChildren(!showChildren)}
              aria-label={`${showChildren ? "Label-Gruppe ausblenden" : "Label-Gruppe anzeigen"} - ${labelGroup.title}`}
            >
              <IconArrowTinyRight className={showChildren ? "rotate-90" : "rotate-0"} />
            </Button>
          </div>
          <div className="shrink-0">
            <IconCircleGroup className="h-3.5 w-3.5" style={{ color: labelGroup.color }} />
          </div>
          <div className="flex-1 text-sm font-medium">{labelGroup.title}</div>
          <div className="flex shrink-0 items-center">
            {hasPermissionToManageWorkspace && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCreateLabelForm(true)}
                  aria-label={`Label zu ${labelGroup.title} hinzufügen`}
                >
                  <IconPlus className="h-3 w-3" />
                </Button>
                <LabelGroupActionButton
                  labelGroup={labelGroup}
                  workspace={{ id: workspaceId }}
                  handleEditLabelGroup={() => setEditMode(true)}
                  asChild
                >
                  <MenuDotsButton aria-label={`Label-Gruppe ${labelGroup.title} Aktionen öffnen`} />
                </LabelGroupActionButton>
              </>
            )}
          </div>
        </div>
      )}
      {editMode && (
        <LabelGroupEditForm
          labelGroup={labelGroup}
          onCanceled={() => {
            setLocalAllowMultiSelect(labelGroup.allowMultiSelect);
            setEditMode(false);
          }}
          onSuccess={() => {
            setEditMode(false);
          }}
          onAllowMultiSelectChange={setLocalAllowMultiSelect}
        />
      )}
      {showCreateLabelForm && hasPermissionToManageWorkspace && (
        <LabelCreateForm
          workspaceId={workspaceId}
          labelGroupId={labelGroup.id}
          defaultLabelColor={labelGroup.color}
          onCanceled={() => {
            setShowCreateLabelForm(false);
          }}
          onSuccess={() => {
            handleLabelHasBeenAdded();
          }}
          className="pl-1"
        />
      )}
      {showChildren && (
        <div
          className="border-t border-border px-2 pb-1"
          {...(!localAllowMultiSelect && {
            role: "radiogroup",
            "aria-label": labelGroup.title,
          })}
        >
          {childLabelList &&
            childLabelList.map((label) => {
              return label ? (
                <LabelListItem
                  key={label.id}
                  labelId={label.id}
                  workspaceId={workspaceId}
                  labelAssignmentContext={labelAssignmentContext}
                  allowMultiSelect={localAllowMultiSelect}
                />
              ) : (
                <></>
              );
            })}
        </div>
      )}
    </div>
  );
};
